import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, Truck, Users, ClipboardList, Menu, X, LogOut,
  ChevronRight, UserCog, MapPin, FileBarChart, BarChart3, Building2,
  ChevronDown, Check, Globe, Fuel, Settings, Factory, ClipboardCheck,
  BarChart4, Shield, ArrowLeft, Package, Wallet, Sun, Moon, Monitor, Boxes, CreditCard, Layers, ScanSearch
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';
import { DescritivoTecnicoModal } from './DescritivoTecnicoModal';

const tenantMenuItems = [
  { path: '/dashboard', label: 'Painel', icon: LayoutDashboard, roles: ['admin', 'gestor', 'auxiliar_administrativo', 'supervisor', 'operador'] },
  { path: '/productivity', label: 'Produtividade', icon: BarChart3, roles: ['admin', 'gestor', 'supervisor'], permModule: 'relatorios' },
  { path: '/machines', label: 'Máquinas e Veículos', icon: Truck, roles: ['admin', 'gestor', 'supervisor', 'tecnico'], permModule: 'maquinas' },
  { path: '/operators', label: 'Operadores', icon: Users, roles: ['admin', 'gestor', 'supervisor'], permModule: 'operadores' },
  { path: '/colaboradores', label: 'Colaboradores', icon: UserCog, roles: ['admin'] },
  { path: '/locations', label: 'Locais', icon: MapPin, roles: ['admin', 'gestor', 'supervisor'], permModule: 'locais' },
  { path: '/setores-operacao', label: 'Setores de Operacao', icon: Layers, roles: ['admin', 'gestor', 'supervisor'], permModule: 'locais' },
  { path: '/work-orders', label: 'Ordens de Servico', icon: ClipboardList, roles: ['admin', 'gestor', 'supervisor', 'tecnico', 'operador'], permModule: 'ordens_servico' },
  { path: '/fuel', label: 'Combustivel', icon: Fuel, roles: ['admin', 'gestor', 'auxiliar_administrativo', 'supervisor', 'tecnico'], permModule: 'combustivel' },
  { path: '/fornecedores', label: 'Fornecedores', icon: Package, roles: ['admin', 'gestor'], permModule: 'combustivel' },
  { path: '/almoxarifado', label: 'Almoxarifado', icon: Boxes, roles: ['admin', 'gestor', 'auxiliar_administrativo', 'supervisor'] },
  { path: '/contas-a-pagar', label: 'Contas a Pagar', icon: CreditCard, roles: ['admin', 'gestor', 'auxiliar_administrativo'] },
  { path: '/conta-corrente', label: 'Conta Corrente', icon: Wallet, roles: ['admin', 'gestor', 'auxiliar_administrativo'] },
  { 
    path: '/producao', label: 'Producao', icon: Factory, roles: ['admin', 'gestor', 'auxiliar_administrativo', 'supervisor'],
    module: 'producao', permModule: 'producao',
    children: [
      { path: '/producao/apontamento', label: 'Apontamento', icon: ClipboardCheck },
      { path: '/producao/equiparacao', label: 'Rel. Equiparacao', icon: BarChart4 }
    ]
  },
  { path: '/reports', label: 'Relatorios', icon: FileBarChart, roles: ['admin', 'gestor', 'supervisor'], permModule: 'relatorios' },
  { path: '/users', label: 'Usuarios', icon: UserCog, roles: ['admin'], permModule: 'usuarios_tenant' },
  { path: '/operation-types', label: 'Tipos de Operacao', icon: Settings, roles: ['admin'], permModule: 'tipos_operacao' },
  { 
    path: '/financeiro', label: 'Financeiro', icon: Wallet, roles: ['admin', 'auxiliar_administrativo'],
    permModule: 'financeiro', requireFinanceiro: true,
    children: [
      { path: '/financeiro', label: 'Dashboard', icon: BarChart3 },
      { path: '/financeiro/lancamentos', label: 'Lancamentos', icon: ClipboardList },
      { path: '/financeiro/plano-de-contas', label: 'Plano de Contas', icon: Settings },
      { path: '/financeiro/inventario', label: 'Inventario de Ativos', icon: Package },
      { path: '/financeiro/centros-custo', label: 'Centros de Custo', icon: Wallet },
    ]
  },
];

const roleLabels = {
  admin: 'Administrador', gestor: 'Gestor', supervisor: 'Supervisor',
  tecnico: 'Tecnico Mecanico', operador: 'Operador'
};

function ThemeToggleButton() {
  const { mode, theme, toggleTheme } = useTheme();
  // mode = 'dark' | 'light' | 'system'
  const ICONS = { dark: Sun, light: Moon, system: Monitor };
  const LABELS = {
    dark: 'Tema escuro (clique para claro)',
    light: 'Tema claro (clique para automático)',
    system: `Automático — seguindo sistema (${theme}). Clique para escuro.`,
  };
  const Icon = ICONS[mode] || Sun;
  return (
    <button
      onClick={toggleTheme}
      data-testid="theme-toggle-button"
      data-theme-mode={mode}
      aria-label={LABELS[mode]}
      title={LABELS[mode]}
      className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-yellow-400 hover:text-yellow-300 transition-colors"
    >
      <Icon size={18} />
    </button>
  );
}

function TenantSelector({ onTenantSwitch }) {
  const { tenants, selectedTenantId, currentTenantName } = useAuth();
  const [open, setOpen] = useState(false);
  if (!tenants.length) return null;

  return (
    <div className="relative px-4 py-3 border-b border-gray-800">
      <button onClick={() => setOpen(!open)} data-testid="tenant-selector-button"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0A0A0A] border border-gray-700 hover:border-yellow-500/50 transition-colors text-left">
        <Building2 size={16} className="text-yellow-500 shrink-0" />
        <span className="text-sm text-white truncate flex-1">{currentTenantName || 'Selecionar empresa'}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-4 right-4 top-full mt-1 z-50 bg-[#1A1A1A] border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
            {tenants.map(t => (
              <button key={t.id} data-testid={`tenant-option-${t.id}`}
                onClick={() => { onTenantSwitch(t.id); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors hover:bg-gray-800 ${
                  t.id === selectedTenantId ? 'text-yellow-500' : 'text-gray-300'
                }`}>
                <span className="truncate flex-1">{t.name}</span>
                {t.is_demo && <span className="text-xs px-1.5 py-0.5 bg-yellow-500/10 text-yellow-500 rounded">Demo</span>}
                {t.id === selectedTenantId && <Check size={14} className="text-yellow-500 shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NavLink({ item, isActive, onClick }) {
  return (
    <Link to={item.path} onClick={onClick} data-testid={`nav-${item.path.slice(1)}`}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
        isActive ? 'bg-yellow-500 text-black font-semibold' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
      }`}>
      <item.icon size={20} />
      <span>{item.label}</span>
      {isActive && <ChevronRight size={16} className="ml-auto" />}
    </Link>
  );
}

function NavSubmenu({ item, isActive, isParentActive, openSubmenu, setOpenSubmenu, onClose }) {
  const isOpen = openSubmenu === item.path || isParentActive;
  return (
    <div>
      <button onClick={() => setOpenSubmenu(openSubmenu === item.path ? null : item.path)}
        data-testid={`nav-${item.path.slice(1)}`}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
          isParentActive ? 'bg-yellow-500/10 text-yellow-500' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
        }`}>
        <item.icon size={20} />
        <span>{item.label}</span>
        <ChevronDown size={14} className={`ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="ml-4 mt-1 space-y-1 border-l border-gray-800 pl-3">
          {item.children.map(child => (
            <Link key={child.path} to={child.path} onClick={onClose}
              data-testid={`nav-${child.path.replace(/\//g, '-').slice(1)}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                isActive(child.path) ? 'bg-yellow-500 text-black font-semibold' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}>
              <child.icon size={16} />
              <span>{child.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children, user, onLogout }) {
  const { tenants, selectedTenantId, inTenantContext, isMasterAuxiliar, hasPermission, enterTenant, exitTenantContext, switchTenant, currentTenantName } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [descritivoOpen, setDescritivoOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [tenantModules, setTenantModules] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const isMaster = user?.is_master && tenants.length > 0;
  const isActive = (path) => location.pathname === path;
  const isParentActive = (item) => item.children?.some(c => location.pathname === c.path);

  // Fetch tenant modules when in tenant context
  React.useEffect(() => {
    if (!selectedTenantId || !inTenantContext) return;
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/${selectedTenantId}/modulos`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Tenant-Id': selectedTenantId }
    }).then(r => r.ok ? r.json() : {}).then(d => setTenantModules(d.modulos_ativos || {})).catch(() => {});
  }, [selectedTenantId, inTenantContext]);

  // Build tenant menu items (only when inside a tenant)
  const getFilteredMenuItems = () => {
    if (isMaster && !inTenantContext) return [];
    if (!isMaster) {
      return tenantMenuItems.filter(item => {
        if (!item.roles.includes(user?.role)) return false;
        if (item.module && !tenantModules[item.module]) return false;
        if (item.requireFinanceiro && !user?.acesso_financeiro && !user?.is_master) return false;
        return true;
      });
    }
    // Master inside tenant context
    return tenantMenuItems.filter(item => {
      const effectiveRole = 'admin';
      if (!item.roles.includes(effectiveRole)) return false;
      if (item.module && !tenantModules[item.module]) return false;
      if (isMasterAuxiliar && item.permModule && !hasPermission(item.permModule, 'visualizar')) return false;
      return true;
    });
  };

  const menuItems = getFilteredMenuItems();
  const closeSidebar = () => setSidebarOpen(false);

  const handleBackToMaster = () => {
    exitTenantContext();
    navigate('/overview');
    closeSidebar();
  };

  const handleTenantSwitch = (tenantId) => {
    if (inTenantContext) {
      enterTenant(tenantId);
    } else {
      switchTenant(tenantId);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#111] border-b border-gray-800 z-50 flex items-center justify-between px-4">
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid="mobile-menu-button">
          <Menu size={24} />
        </button>
        <img src="/logo-impulso-digital.png" alt="Impulso Digital" className="h-8" />
        <div className="flex items-center gap-2">
          <NotificationBell canSee={user?.role === 'admin' || user?.is_master} />
          <ThemeToggleButton />
        </div>
      </header>

      {sidebarOpen && <div className="lg:hidden fixed inset-0 bg-black/60 z-50" onClick={closeSidebar} />}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#111] border-r border-gray-800 z-50 transform transition-transform duration-300 ease-in-out flex flex-col lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800 shrink-0">
          <img src="/logo-impulso-digital.png" alt="Impulso Digital" className="h-8" />
          <button onClick={closeSidebar} className="lg:hidden p-2 text-gray-400 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Tenant Selector — only when inside tenant context */}
        {isMaster && inTenantContext && <TenantSelector onTenantSwitch={handleTenantSwitch} />}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* === MASTER CONTEXT === */}
          {isMaster && !inTenantContext && (
            <>
              <NavLink item={{ path: '/overview', label: 'Visao Geral', icon: Globe }} isActive={isActive('/overview')} onClick={closeSidebar} />
              {!isMasterAuxiliar && (
                <>
                  <NavLink item={{ path: '/tenants', label: 'Empresas', icon: Building2 }} isActive={isActive('/tenants')} onClick={closeSidebar} />
                  <NavLink item={{ path: '/master/usuarios', label: 'Usuarios Master', icon: Shield }} isActive={isActive('/master/usuarios')} onClick={closeSidebar} />
                  <NavLink item={{ path: '/master/raiox', label: 'Raio-X da Empresa', icon: ScanSearch }} isActive={isActive('/master/raiox')} onClick={closeSidebar} />
                  <button
                    onClick={() => { closeSidebar(); setDescritivoOpen(true); }}
                    data-testid="nav-descritivo-tecnico"
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    <FileBarChart size={20} />
                    <span>Descritivo Tecnico</span>
                  </button>
                </>
              )}
              {isMasterAuxiliar && hasPermission('empresas', 'visualizar') && (
                <NavLink item={{ path: '/tenants', label: 'Empresas', icon: Building2 }} isActive={isActive('/tenants')} onClick={closeSidebar} />
              )}
            </>
          )}

          {/* === TENANT CONTEXT — back button + tenant items === */}
          {isMaster && inTenantContext && (
            <>
              <button onClick={handleBackToMaster} data-testid="back-to-master-btn"
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-yellow-500 border border-yellow-500/30 hover:bg-yellow-500/10 transition-all duration-200 mb-2">
                <ArrowLeft size={20} />
                <span>Voltar ao Master</span>
              </button>
              {currentTenantName && (
                <div className="px-4 py-2 mb-2">
                  <p className="text-xs text-gray-500 uppercase font-medium">Empresa</p>
                  <p className="text-sm text-white font-medium truncate">{currentTenantName}</p>
                </div>
              )}
              <div className="border-t border-gray-800 mb-2" />
            </>
          )}

          {/* Tenant-level items (for tenant users always, for master only in tenant context) */}
          {menuItems.map((item) => (
            item.children ? (
              <NavSubmenu key={item.path} item={item} isActive={isActive} isParentActive={isParentActive(item)}
                openSubmenu={openSubmenu} setOpenSubmenu={setOpenSubmenu} onClose={closeSidebar} />
            ) : (
              <NavLink key={item.path} item={item} isActive={isActive(item.path)} onClick={closeSidebar} />
            )
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-gray-800 shrink-0">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center text-black font-bold">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-gray-500 text-xs truncate">
                {isMasterAuxiliar ? 'Auxiliar Master' : (roleLabels[user?.role] || user?.role)}
                {user?.is_master && !isMasterAuxiliar && <span className="ml-1 text-yellow-500">(Master)</span>}
              </p>
            </div>
            <ThemeToggleButton />
            <NotificationBell canSee={user?.role === 'admin' || user?.is_master} />
          </div>
          <button onClick={onLogout} data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors">
            <LogOut size={18} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">{children}</div>
      </main>

      <DescritivoTecnicoModal open={descritivoOpen} onClose={() => setDescritivoOpen(false)} />
    </div>
  );
}
